import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, FileText, FolderOpen, AlertCircle, UserCircle } from "lucide-react";

interface LoadingStateProps {
  message?: string;
  testId?: string;
  fullPage?: boolean;
}

export function LoadingState({ 
  message = "Loading...", 
  testId = "loading-state",
  fullPage = false 
}: LoadingStateProps) {
  if (fullPage) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center" data-testid={testId}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{message}</p>
        </div>
      </div>
    );
  }
  
  return (
    <Card data-testid={testId}>
      <CardContent className="py-12">
        <div className="flex flex-col items-center justify-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{message}</p>
        </div>
      </CardContent>
    </Card>
  );
}

interface EmptyStateProps {
  icon?: JSX.Element;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    testId?: string;
  };
  testId?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  testId = "empty-state"
}: EmptyStateProps) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-8 text-center">
        {icon && (
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-muted p-3">
              {icon}
            </div>
          </div>
        )}
        <p className="font-medium">{title}</p>
        {description && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
        {action && (
          <Button
            variant="outline"
            className="mt-4"
            onClick={action.onClick}
            data-testid={action.testId || "button-empty-action"}
          >
            {action.label}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

interface ErrorStateProps {
  title?: string;
  message: string;
  retry?: {
    label?: string;
    onClick: () => void;
    testId?: string;
  };
  testId?: string;
}

export function ErrorState({
  title = "Error",
  message,
  retry,
  testId = "error-state"
}: ErrorStateProps) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-destructive/10 p-3">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
        </div>
        <p className="font-medium text-destructive">{title}</p>
        <p className="text-sm text-muted-foreground mt-1">{message}</p>
        {retry && (
          <Button
            variant="outline"
            className="mt-4"
            onClick={retry.onClick}
            data-testid={retry.testId || "button-retry"}
          >
            {retry.label || "Try Again"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

interface NoClientSelectedProps {
  entityName?: string;
  testId?: string;
}

export function NoClientSelected({
  entityName = "data",
  testId = "no-client-state"
}: NoClientSelectedProps) {
  return (
    <div className="flex-1 p-6 flex items-center justify-center" data-testid={testId}>
      <div className="text-center">
        <div className="flex justify-center mb-3">
          <div className="rounded-full bg-muted p-3">
            <UserCircle className="h-6 w-6 text-muted-foreground" />
          </div>
        </div>
        <p className="font-medium">No client selected</p>
        <p className="text-sm text-muted-foreground mt-1">Please select a client to view {entityName}.</p>
      </div>
    </div>
  );
}
